import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import BackendService from 'BackendService';
import React from 'react';
import FundOpportunity from '../../models/FundOpportunity';
import PropertyCardGroup from '../PropertyCardGroup';
import PropertyFilteredList from '../PropertyFilteredList';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';

interface PropertiesActiveTabsProps {
  onItemClick?: (link: string) => void;
  wrapperClassName?: string;
  tone?: keyof typeof Palette;
  propertyCardTone?: keyof typeof Palette;
  noSpacing?: boolean;
  onAddClicked: () => void;
}

const COMPLETE_PAGE_SIZE = 20;

const LoadingState = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress />
  </Box>
);

export default function PropertiesActiveTabs({
  tone,
  propertyCardTone,
  noSpacing,
  onAddClicked,
}: PropertiesActiveTabsProps) {
  const [tab, setTab] = React.useState(0);
  const [activatedTabs, setActivatedTabs] = React.useState<Set<number>>(() => new Set([0]));
  const [completeFunds, setCompleteFunds] = React.useState<FundOpportunity[]>([]);
  const [completePagination, setCompletePagination] = React.useState({
    page: 1,
    limit: COMPLETE_PAGE_SIZE,
    total: 0,
    hasMore: false,
  });
  const [completeLoading, setCompleteLoading] = React.useState(false);
  const [completeLoaded, setCompleteLoaded] = React.useState(false);

  const eoiOpenStatuses = React.useMemo(
    () => ['EOI', 'APPLICATION_OPEN'] as FundOpportunity['status'][],
    [],
  );
  const underManagementStatuses = React.useMemo(
    () => ['UNDER_MANAGEMENT'] as FundOpportunity['status'][],
    [],
  );

  const loadCompleteFunds = React.useCallback(async (page: number) => {
    setCompleteLoading(true);

    try {
      const result = await new BackendService().getPagedFundOpportunitiesByStatus(
        'COMPLETE',
        page,
        COMPLETE_PAGE_SIZE,
      );

      setCompleteFunds((current) => (page === 1 ? result.data : [...current, ...result.data]));
      if (result.pagination) {
        setCompletePagination(result.pagination);
      }
      setCompleteLoaded(true);
    } finally {
      setCompleteLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (tab !== 2 || completeLoaded || completeLoading) {
      return;
    }

    void loadCompleteFunds(1);
  }, [tab, completeLoaded, completeLoading, loadCompleteFunds]);

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setTab(value);
    setActivatedTabs((current) => {
      const next = new Set(current);
      next.add(value);
      return next;
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': { fontWeight: 600 },
            '& .MuiTab-root.Mui-selected': { color: Palette.default.buttonBg },
            '& .MuiTabs-indicator': { backgroundColor: Palette.default.buttonBg },
          }}
        >
          <Tab label="EOI Open" value={0} />
          <Tab label="Under Management" value={1} />
          <Tab
            label={
              completeLoaded
                ? `Complete (${completePagination.total || completeFunds.length})`
                : 'Complete'
            }
            value={2}
          />
        </Tabs>
        <StyledButton onClick={onAddClicked} variant="contained" color="primary" noTopSpacing>
          Create Opportunity
        </StyledButton>
      </Box>

      <Box role="tabpanel" hidden={tab !== 0} sx={{ pt: 2 }}>
        <PropertyFilteredList
          filterStatus={eoiOpenStatuses}
          title="EOI Open"
          description="Funds currently accepting EOI and Applications"
          tone={tone}
          propertyCardTone={propertyCardTone}
          noSpacing={noSpacing}
          firstElement
          linkable={true}
        />
      </Box>

      <Box role="tabpanel" hidden={tab !== 1} sx={{ pt: 2 }}>
        {activatedTabs.has(1) ? (
          <PropertyFilteredList
            filterStatus={underManagementStatuses}
            title="Under Management"
            description="Funds currently under management"
            tone={tone}
            propertyCardTone={propertyCardTone}
            noSpacing={noSpacing}
            firstElement
            linkable={true}
          />
        ) : null}
      </Box>

      <Box role="tabpanel" hidden={tab !== 2} sx={{ pt: 2 }}>
        {activatedTabs.has(2) ? (
          completeLoading && !completeLoaded ? (
            <LoadingState />
          ) : completeFunds.length === 0 ? (
            <Typography color="text.secondary">No complete funds found.</Typography>
          ) : (
            <>
              <PropertyCardGroup
                fundOpportunities={completeFunds}
                sourceType="fundOpportunity"
                title="Complete Funds"
                description={
                  completePagination.total > 0
                    ? `Showing ${completeFunds.length} of ${completePagination.total} complete funds`
                    : undefined
                }
                tone={tone}
                propertyCardTone={propertyCardTone}
                noSpacing={noSpacing}
                linkable={true}
                contentAware={true}
                preventFullSizePopup={true}
                firstElement
              />
              {completePagination.hasMore ? (
                <StyledButton
                  onClick={() => void loadCompleteFunds(completePagination.page + 1)}
                  disabled={completeLoading}
                  variant="contained"
                  color="primary"
                >
                  {completeLoading ? 'Loading...' : 'Load more'}
                </StyledButton>
              ) : null}
            </>
          )
        ) : null}
      </Box>
    </>
  );
}
